import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import {
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { addLineCollection } from "@insite/client-framework/Services/CartService";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import loadCurrentPromotions from "@insite/client-framework/Store/Data/Promotions/Handlers/LoadCurrentPromotions";
import { CartLineCollectionModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { GetQuoteToCartApiParameter, getQuoteToCartCollection } from "@store/Services/QuoteToCartService";
import { StoreHandlerProps } from "@store/Store/HandlerCreator";
import { QuoteToCartModel } from "@store/Types/QuoteToCart";

export interface QuoteToCartResult {
    quote?: QuoteToCartModel;
    cartLines?: CartLineCollectionModel;
}
export interface QuoteToCartLineModel {
    id: string;
    qtyOrdered: number | null;
    unitOfMeasure: string;
}
type Parameter = {
    apiParameter: GetQuoteToCartApiParameter;
    onSegmentComplete?: (nbComplete:number, nbTotal:number) => void;
} & HasOnSuccess<QuoteToCartResult> &
    HasOnError<any>;
type Props = StoreHandlerProps & {
    apiParameter: GetQuoteToCartApiParameter;
    apiResult: QuoteToCartResult;
};

type HandlerType = Handler<Parameter, Props>;

export const PopulateApiParameter: HandlerType = props => {
    const { onError, ...parameter } = props.parameter;
    props.apiParameter = props.parameter.apiParameter;
};

export const DispatchBeginLoadQuoteToCart: HandlerType = props => {
    props.dispatch({
        type: "Store/Data/QuoteToCart/BeginLoadQuote",
        parameter: props.apiParameter,
    });

    props.apiResult = {};
};
export const RequestDataFromApi: HandlerType = async props => {
    props.apiResult.quote = await getQuoteToCartCollection(props.apiParameter);
};

export const AddAllToCart: HandlerType = async props => {
    const cartLines: QuoteToCartLineModel[] = [];
    props.apiResult.quote?.quoteLines.forEach(quoteLine => {
        cartLines.push({
            productId: quoteLine.productId,
            qtyOrdered: quoteLine.quantityOrdered,
            unitOfMeasure: quoteLine.unitOfMeasure,
        } as CartLineModel);
    });

    let allLoadedProducts = false;
    if (cartLines.length) {
        const size = 24;
        let nbLoaded = 0;
        const nbToLoad = Math.ceil(cartLines.length/size);
        let allResults:CartLineCollectionModel | undefined;

        for (let i = 0; i < cartLines.length; i += size) {
            const cartLinesChunk = cartLines.slice(i, i + size);
            let loadedProducts = false;
            const param = {
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: { cartLines: cartLinesChunk } as CartLineCollectionModel,
                },
            };

            const result = await addLineCollection(param.apiParameter);
            if (allResults) {
                result?.cartLines?.forEach(element => {
                    allResults?.cartLines?.push(element);
                });
            } else {
                allResults = result;
            }

            nbLoaded++;
            loadedProducts = true;
            if(nbLoaded >= nbToLoad) {
                allLoadedProducts = true;
            }
            
            await waitFor(() => loadedProducts);
            props.parameter.onSegmentComplete?.(i+cartLinesChunk.length, cartLines.length);
        }
    } else {
        props.parameter.onError?.(props.apiResult);
        allLoadedProducts = true;
    }
    await waitFor(() => allLoadedProducts);
    props.dispatch(loadCurrentCart());
    props.dispatch(loadCurrentPromotions());
};

export const DispatchCompleteLoadQuoteToCart: HandlerType = props => {
    props.dispatch({
        type: "Store/Data/QuoteToCart/CompleteLoadQuote",
        parameter: props.apiParameter,
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.cartLines?.cartLines?.length) {
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const chain = [
    PopulateApiParameter,
    DispatchBeginLoadQuoteToCart,
    RequestDataFromApi,
    AddAllToCart,
    DispatchCompleteLoadQuoteToCart,
    ExecuteOnSuccessCallback,
];

const loadQuoteToCart = createHandlerChainRunner(chain, "LoadQuoteToCart");
export default loadQuoteToCart;
